import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Drawer, Popconfirm, Row, Space, Table, Typography, Card, Tooltip, Tag, Select, Input, Tabs, Switch } from 'antd';
import { SearchOutlined, CopyOutlined } from '@ant-design/icons';
import { apiFetch } from 'services/apiClient';
import { debounce } from 'lodash';
import { openNotification } from '../../../utils';
import TrashIcon from 'assets/icons/trash.svg';
import PenIcon from 'assets/icons/pen.svg';
import { useLocation } from 'react-router-dom';

import { listProfessionsAll, deleteProfession, createProfession, getProfession, updateProfession } from '../services/professionService';
import { listMeasuringInstrumentsAll } from '../../measuring-instruments/services/measuring-instrumentService';
import ProfessionForm from './Form';
import { CreateProfessionDto } from '../types/Profession';

type StatusTabKey = 'active' | 'passive';

export default function ProfessionList({ referenceId }: { referenceId?: number }) {
  const { Text } = Typography;

  const location = useLocation();
  const isActive = location.pathname === '/settings/professions';
  const wasActiveRef = useRef(isActive);

  const [data, setData] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | undefined>();
  // Filters
  const [filterOpen, setFilterOpen] = useState(false);
  const [attrOptions, setAttrOptions] = useState<any[]>([]);
  const [measuringInstrumentOptions, setMeasuringInstrumentOptions] = useState<any[]>([]);
  // committed filters
  const [selectedAttrIds, setSelectedAttrIds] = useState<number[]>([]);
  const [selectedMeasuringInstrumentIds, setSelectedMeasuringInstrumentIds] = useState<number[]>([]);
  // pending filters (drawer)
  const [pendingAttrIds, setPendingAttrIds] = useState<number[]>([]);
  const [pendingMeasuringInstrumentIds, setPendingMeasuringInstrumentIds] = useState<number[]>([]);
  // Search
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [copyingId, setCopyingId] = useState<number | null>(null);
  const [statusTab, setStatusTab] = useState<StatusTabKey>('active');
  const [statusUpdatingId, setStatusUpdatingId] = useState<number | null>(null);
  const debouncedApplySearch = React.useMemo(() => debounce((val: string) => {
    setSearch(val);
  }, 300), []);
  React.useEffect(() => () => { debouncedApplySearch.cancel(); }, [debouncedApplySearch]);

  const resetFilters = useCallback(() => {
    debouncedApplySearch.cancel();
    setSelectedAttrIds([]);
    setPendingAttrIds([]);
    setSelectedMeasuringInstrumentIds([]);
    setPendingMeasuringInstrumentIds([]);
    setSearchInput('');
    setSearch('');
  }, [debouncedApplySearch]);

  useEffect(() => {
    if (!isActive && wasActiveRef.current) {
      resetFilters();
      setStatusTab('active');
    }
    wasActiveRef.current = isActive;
  }, [isActive, resetFilters]);

  const toNumberArray = (values: any[]): number[] =>
    values
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value));

  const getAttributeIds = (record: any): number[] => {
    const attrs: any[] = Array.isArray(record?.attributes) ? record.attributes : [];
    return attrs
      .map((attr: any) => (typeof attr === 'object' ? attr?.id : attr))
      .map((id: any) => Number(id))
      .filter((id: number) => Number.isFinite(id));
  };

  const getMeasuringInstrumentIds = (record: any): number[] => {
    const instruments: any[] = Array.isArray(record?.measuringInstruments) ? record.measuringInstruments : [];
    return instruments
      .map((instrument: any) => (typeof instrument === 'object' ? instrument?.id : instrument))
      .map((id: any) => Number(id))
      .filter((id: number) => Number.isFinite(id));
  };

  const getAttributeCount = (record: any): number => getAttributeIds(record).length;
  const getMeasuringInstrumentCount = (record: any): number => getMeasuringInstrumentIds(record).length;

  const fetchData = useCallback(async () => {
    const [professionList, attributes, measuringInstruments] = await Promise.all([
      listProfessionsAll('name,asc'),
      apiFetch('/attributes?isActive=true'),
      listMeasuringInstrumentsAll('isActive.equals=true'),
    ]);
    setData(Array.isArray(professionList) ? professionList : []);
    setAttrOptions(Array.isArray(attributes) ? attributes : []);
    const sortedMeasuringInstruments = (Array.isArray(measuringInstruments) ? measuringInstruments : [])
      .slice()
      .sort((a: any, b: any) =>
        String(a?.name ?? '')
          .toLocaleLowerCase('tr-TR')
          .localeCompare(String(b?.name ?? '').toLocaleLowerCase('tr-TR'), 'tr'),
      );
    setMeasuringInstrumentOptions(sortedMeasuringInstruments);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  const openCreate = () => { setEditingId(undefined); setOpen(true); };
  const openEdit = (id: number) => { setEditingId(id); setOpen(true); };
  const handleClose = () => setOpen(false);
  const openFilter = () => {
    setPendingAttrIds(selectedAttrIds);
    setPendingMeasuringInstrumentIds(selectedMeasuringInstrumentIds);
    setFilterOpen(true);
  };
  const closeFilter = () => setFilterOpen(false);
  const clearFilter = () => {
    setPendingAttrIds([]);
    setPendingMeasuringInstrumentIds([]);
    setSelectedAttrIds([]);
    setSelectedMeasuringInstrumentIds([]);
  };
  const handleDelete = async (id: number) => {
    try {
      await deleteProfession(id);
      openNotification('success', 'Başarılı', 'Meslek Silindi');
      await fetchData();
    } catch (e) {
      openNotification('error', 'Hata', 'Meslek silme işlemi başarısız');
    }
  };
  const handleCopy = async (id: number) => {
    if (typeof id !== 'number') return;
    try {
      setCopyingId(id);
      const existing = await getProfession(id);
      const existingName = typeof existing?.name === 'string' && existing.name.length ? existing.name.trim() : 'Meslek';
      const baseName = existingName.length ? existingName : 'Meslek';
      const copySuffix = baseName.toLocaleLowerCase('tr-TR').includes('kopya') ? ' - Kopya' : ' Kopya';
      let newName = `${baseName}${copySuffix}`;
      const currentNames = (data || []).map((p: any) => (typeof p?.name === 'string' ? p.name.trim() : '')).filter(Boolean);
      let counter = 2;
      while (currentNames.includes(newName)) {
        newName = `${baseName}${copySuffix} ${counter}`;
        counter += 1;
      }
      const attributeIds = Array.isArray(existing?.attributes)
        ? existing.attributes
          .map((attr: any) => (typeof attr === 'object' ? attr?.id ?? null : attr))
          .filter((attrId: any) => attrId != null)
        : [];
      const payload: CreateProfessionDto = {
        name: newName,
        isActive: existing?.isActive === undefined ? true : existing.isActive,
        attributes: attributeIds,
      };
      await createProfession(payload);
      openNotification('success', 'Başarılı', 'Meslek kopyalandı');
      await fetchData();
    } catch (error) {
      console.error('Meslek kopyalama hatası', error);
      openNotification('error', 'Hata', 'Meslek kopyalanamadı');
    } finally {
      setCopyingId(null);
    }
  };
  const handleStatusToggle = async (record: any, nextValue: boolean) => {
    if (typeof record?.id !== 'number') return;
    try {
      setStatusUpdatingId(record.id);
      const attributeIds = Array.isArray(record?.attributes)
        ? record.attributes
          .map((attr: any) => {
            if (attr == null) return null;
            if (typeof attr === 'object') return attr?.id ?? null;
            return attr;
          })
          .filter((attrId: any) => typeof attrId === 'number')
        : [];
      const nameValue = typeof record?.name === 'string'
        ? record.name
        : (record?.name?.toString ? record.name.toString() : '');
      const payload: CreateProfessionDto = {
        name: nameValue,
        isActive: nextValue,
        attributes: attributeIds,
      };
      await updateProfession(record.id, payload);
      openNotification('success', 'Başarılı', nextValue ? 'Meslek aktif edildi' : 'Meslek pasif edildi');
      await fetchData();
    } catch (error) {
      console.error('Meslek durum güncelleme hatası', error);
      openNotification('error', 'Hata', 'Durum güncellenemedi');
    } finally {
      setStatusUpdatingId(null);
    }
  };
  const renderAttributeNames = (record: any) => {
    const attrs: any[] = Array.isArray(record?.attributes) ? record.attributes : [];
    const names = attrs
      .map((a: any) => (typeof a === 'object' ? a?.name : a))
      .filter((n: any) => !!n)
      .map((n: any) => String(n));
    if (!names.length) return [];
    return names.slice().sort((a: string, b: string) => a.localeCompare(b, 'tr'));
  };

  const renderMeasuringInstrumentNames = (record: any) => {
    const instruments: any[] = Array.isArray(record?.measuringInstruments) ? record.measuringInstruments : [];
    const names = instruments
      .map((mi: any) => (mi && typeof mi === 'object' ? mi?.name : mi))
      .filter((name: any) => typeof name === 'string' && name.trim().length > 0)
      .map((name: string) => name.trim());
    if (!names.length) return [];
    return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b, 'tr'));
  };

  const normalizedSearch = search.trim().toLocaleLowerCase('tr-TR');
  const filteredData = data.filter((record: any) => {
    const isActiveFilter = statusTab === 'active';
    const matchesStatus = !!record?.isActive === isActiveFilter;
    if (!matchesStatus) return false;

    if (selectedAttrIds.length > 0) {
      const attrIds = getAttributeIds(record);
      const matchesAttribute = attrIds.some((id) => selectedAttrIds.includes(id));
      if (!matchesAttribute) return false;
    }

    if (selectedMeasuringInstrumentIds.length > 0) {
      const measuringInstrumentIds = getMeasuringInstrumentIds(record);
      const matchesMeasuringInstrument = measuringInstrumentIds.some((id) => selectedMeasuringInstrumentIds.includes(id));
      if (!matchesMeasuringInstrument) return false;
    }

    if (normalizedSearch.length >= 2) {
      const nameValue = String(record?.name ?? '').toLocaleLowerCase('tr-TR');
      return nameValue.includes(normalizedSearch);
    }

    return true;
  });

  const total = filteredData.length;

  const columns = [
    {
      title: 'Meslek Adı',
      dataIndex: 'name',
      sorter: (a: any, b: any) =>
        String(a?.name ?? '').localeCompare(String(b?.name ?? ''), 'tr'),
      defaultSortOrder: 'ascend' as const,
      width: '300px',
    },
    {
      title: 'Parametre Sayısı',
      width: '150px',
      dataIndex: 'attributes',
      sorter: (a: any, b: any) => getAttributeCount(a) - getAttributeCount(b),
      render: (_: any, r: any) => {
        return getAttributeCount(r);
      }
    },
    {
      title: 'Parametreler',
      dataIndex: 'attributes',
      sorter: false,
      render: (_: any, r: any) => {
        const names = renderAttributeNames(r);
        if (!names.length) return '';
        return (
          <Space size={4} wrap>
            {names.map((n: string) => (
              <Tag key={n}>{n}</Tag>
            ))}
          </Space>
        );
      }
    },
    {
      title: 'Ölçüm Cihazı Sayısı',
      dataIndex: 'measuringInstruments',
      width: '170px',
      sorter: (a: any, b: any) => getMeasuringInstrumentCount(a) - getMeasuringInstrumentCount(b),
      render: (_: any, r: any) => getMeasuringInstrumentCount(r),
    },
    {
      title: 'Ölçüm Cihazı',
      dataIndex: 'measuringInstruments',
      sorter: false,
      width: '280px',
      render: (_: any, r: any) => {
        const names = renderMeasuringInstrumentNames(r);
        if (!names.length) return '';
        return (
          <Space size={4} wrap>
            {names.map((name: string) => (
              <Tag key={name}>{name}</Tag>
            ))}
          </Space>
        );
      }
    },
    {
      title: 'Durum', dataIndex: 'isActive', sorter: false, render: (v: boolean, r: any) => (
        <Switch
          checked={!!v}
          onChange={(val) => handleStatusToggle(r, val)}
          checkedChildren='Aktif'
          unCheckedChildren='Pasif'
          loading={statusUpdatingId === r.id}
        />
      )
    },
    {
      title: 'İşlemler', render: (_: any, r: any) => (
        <Space>
          <Tooltip title='Kopyala'>
            <Button
              icon={<CopyOutlined />}
              shape="circle"
              onClick={() => handleCopy(r.id)}
              className="icon-button"
              loading={copyingId === r.id}
              disabled={typeof r?.id !== 'number'}
            />
          </Tooltip>
          <Tooltip title='Düzenle'>
            <Button
              icon={<img src={PenIcon} alt="pen" className="icon" />}
              shape="circle"
              onClick={() => openEdit(r.id)}
              className="icon-button"
            />
          </Tooltip>
          <Popconfirm title='Sil?' onConfirm={() => handleDelete(r.id)}>
            <Tooltip title='Sil'>
              <Button
                icon={<img src={TrashIcon} alt="trash" className="icon" />}
                shape="circle"
                className="icon-button ml-12px"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];
  return (
    <>
      <Card
        className='br-30px kontrolx-card'
        title={(
          <Row align="middle">
            <Tabs
              activeKey={statusTab}
              onChange={(key) => {
                const tabKey: StatusTabKey = key === 'passive' ? 'passive' : 'active';
                setStatusTab(tabKey);
              }}
              className='custom-tabs'
            >
              <Tabs.TabPane
                key='active'
                tab={(
                  <Button
                    type={statusTab === 'active' ? 'primary' : 'default'}
                    className='d-button tab-button'
                  >
                    Aktif Meslek Listesi
                  </Button>
                )}
              />
              <Tabs.TabPane
                key='passive'
                tab={(
                  <Button
                    type={statusTab === 'passive' ? 'primary' : 'default'}
                    className='d-button tab-button'
                  >
                    Pasif Meslek Listesi
                  </Button>
                )}
              />
            </Tabs>
            <Text type="secondary" className="ms-5" style={{ fontWeight: 500, marginTop: 24 }}>
              <strong>({total})</strong> kayıt listelendi
            </Text>
          </Row>
        )}
        extra={
          <Space>
            <Input
              allowClear
              placeholder="Ara"
              prefix={<SearchOutlined style={{ color: '#b8b9bc' }} />}
              className="d-input d-input-form d-input-filter"
              style={{ width: 200 }}
              value={searchInput}
              onChange={(e) => {
                const val = e.target.value || '';
                setSearchInput(val);
                if (val.length >= 2) {
                  debouncedApplySearch(val);
                } else if (val.length === 0) {
                  debouncedApplySearch('');
                }
              }}
            />
            <Button type='primary' onClick={openFilter}>Filtrele</Button>
            <Button type='primary' onClick={openCreate}>Ekle</Button>
          </Space>
        }
      >
        <Table
          rowKey='id'
          dataSource={filteredData}
          columns={columns}
          pagination={false}
        />
      </Card>
      <Drawer open={filterOpen} onClose={closeFilter} destroyOnClose width={420} title='Filtre'>
        <Space direction='vertical' style={{ width: '100%' }} size='large'>
          <div>
            <Typography.Text>Parametre</Typography.Text>
            <Select
              mode='multiple'
              allowClear
              showSearch
              placeholder='Seçiniz'
              style={{ width: '100%', marginTop: 8 }}
              value={pendingAttrIds}
              filterOption={(input, option) => {
                const label = String(option?.children ?? option?.label ?? '');
                return label.toLocaleLowerCase('tr-TR').includes(input.toLocaleLowerCase('tr-TR'));
              }}
              onChange={(vals: any[]) => setPendingAttrIds(toNumberArray(vals))}
            >
              {attrOptions.filter((o: any) => o?.isActive === true).map((o: any) => (
                <Select.Option key={o.id} value={o.id}>{o.name}</Select.Option>
              ))}
            </Select>
          </div>
          <div>
            <Typography.Text>Ölçüm Cihazı</Typography.Text>
            <Select
              mode='multiple'
              allowClear
              showSearch
              placeholder='Seçiniz'
              style={{ width: '100%', marginTop: 8 }}
              value={pendingMeasuringInstrumentIds}
              filterOption={(input, option) => {
                const label = String(option?.children ?? option?.label ?? '');
                return label.toLocaleLowerCase('tr-TR').includes(input.toLocaleLowerCase('tr-TR'));
              }}
              onChange={(vals: any[]) => setPendingMeasuringInstrumentIds(toNumberArray(vals))}
            >
              {measuringInstrumentOptions
                .filter((instrument: any) => instrument?.isActive !== false)
                .map((instrument: any) => (
                  <Select.Option key={instrument.id} value={instrument.id}>
                    {instrument.name}
                  </Select.Option>
                ))}
            </Select>
          </div>
          <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
            <Button onClick={clearFilter}>Temizle</Button>
            <Button
              type='primary'
              onClick={() => {
                setSelectedAttrIds(pendingAttrIds);
                setSelectedMeasuringInstrumentIds(pendingMeasuringInstrumentIds);
                closeFilter();
              }}
            >
              Filtrele
            </Button>
          </Space>
        </Space>
      </Drawer>
      <Drawer open={open} onClose={handleClose} destroyOnClose width={480} title={editingId ? 'Meslek Düzenle' : 'Meslek Ekle'}>
        <ProfessionForm id={editingId} onSuccess={() => { handleClose(); fetchData(); }} />
      </Drawer>
    </>
  );
}
